import { useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  TextInput,
  Textarea,
  Select,
  Checkbox,
  Button,
  ActionIcon,
  Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { Dropzone } from '@mantine/dropzone';
import { Upload, X, File as FileIcon } from 'react-feather';
import { feedbackApi } from '../../services/api/feedback';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface FeedbackModalProps {
  opened: boolean;
  onClose: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FeedbackModal({ opened, onClose }: FeedbackModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    initialValues: {
      feedback_type: '' as string,
      title: '',
      description: '',
      is_blocking: false,
    },
    validate: {
      feedback_type: (value) => (value ? null : 'Please select a feedback type'),
      title: (value) => (value.trim() ? null : 'Title is required'),
      description: (value) => (value.trim() ? null : 'Description is required'),
    },
  });

  const resetAll = () => {
    form.reset();
    setFiles([]);
  };

  const handleClose = () => {
    if (form.isDirty() || files.length > 0) {
      modals.openConfirmModal({
        title: 'Discard Feedback?',
        children: (
          <Text size="sm">
            You have unsaved changes. Are you sure you want to discard this feedback?
          </Text>
        ),
        labels: { confirm: 'Discard', cancel: 'Keep Editing' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          resetAll();
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  const handleFileDrop = (droppedFiles: File[]) => {
    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      notifications.show({
        title: 'File Limit Reached',
        message: `Maximum ${MAX_FILES} files allowed`,
        color: 'yellow',
      });
      return;
    }
    const toAdd = droppedFiles.slice(0, remaining);
    setFiles((prev) => [...prev, ...toAdd]);
  };

  const handleFileReject = () => {
    notifications.show({
      title: 'File Rejected',
      message: `Files must be under ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
      color: 'red',
    });
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values: typeof form.values) => {
    setIsSubmitting(true);
    try {
      await feedbackApi.submit(
        {
          feedback_type: values.feedback_type as 'bug' | 'feature_request',
          title: values.title,
          description: values.description,
          is_blocking: values.is_blocking,
        },
        files,
      );
      notifications.show({
        title: 'Feedback Submitted',
        message: 'Thank you for your feedback!',
        color: 'green',
      });
      resetAll();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Submission Failed',
        message: error instanceof Error ? error.message : 'Failed to submit feedback',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    form.values.feedback_type && form.values.title.trim() && form.values.description.trim();

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Submit Feedback"
      size="lg"
      closeOnClickOutside={false}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Select
            label="Feedback Type"
            placeholder="Select type..."
            required
            data={[
              { value: 'bug', label: 'Bug Report' },
              { value: 'feature_request', label: 'Feature Request' },
            ]}
            {...form.getInputProps('feedback_type')}
          />

          <TextInput
            label="Title"
            placeholder="Brief summary of your feedback"
            required
            {...form.getInputProps('title')}
          />

          <Textarea
            label="Description"
            placeholder="Provide details about the issue or feature request..."
            required
            minRows={4}
            autosize
            {...form.getInputProps('description')}
          />

          {form.values.feedback_type === 'bug' && (
            <Checkbox
              label="This issue is blocking my work"
              {...form.getInputProps('is_blocking', { type: 'checkbox' })}
            />
          )}

          {/* File Attachments */}
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Attachments ({files.length}/{MAX_FILES})
            </Text>
            {files.length < MAX_FILES && (
              <Dropzone
                onDrop={handleFileDrop}
                onReject={handleFileReject}
                maxSize={MAX_FILE_SIZE}
                disabled={isSubmitting}
              >
                <Group justify="center" gap="xl" mih={80} style={{ pointerEvents: 'none' }}>
                  <Stack align="center" gap="xs">
                    <Upload size={24} />
                    <Text size="sm" c="dimmed">
                      Drag files here or click to browse
                    </Text>
                    <Text size="xs" c="dimmed">
                      Max {MAX_FILES} files, {MAX_FILE_SIZE / (1024 * 1024)}MB each
                    </Text>
                  </Stack>
                </Group>
              </Dropzone>
            )}

            {files.length > 0 && (
              <Stack gap="xs" mt="sm">
                {files.map((file, index) => (
                  <Group
                    key={`${file.name}-${file.size}`}
                    justify="space-between"
                    p="xs"
                    style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 6 }}
                  >
                    <Group gap="sm">
                      <FileIcon size={16} />
                      <Box>
                        <Text size="sm" lineClamp={1}>
                          {file.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatFileSize(file.size)}
                        </Text>
                      </Box>
                    </Group>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => removeFile(index)}
                      disabled={isSubmitting}
                      aria-label={`Remove ${file.name}`}
                    >
                      <X size={14} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            )}
          </Box>

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            {(form.isDirty() || files.length > 0) && (
              <Button variant="subtle" color="red" onClick={resetAll} disabled={isSubmitting}>
                Clear All
              </Button>
            )}
            <Button
              type="submit"
              color="medleyGreen"
              loading={isSubmitting}
              disabled={!isFormValid}
            >
              Submit Feedback
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
